import type { TFn } from "../i18nTypes";

// PG-56 review (25/09): the container's own accessible summary, announced
// once, states more than the bare lo-hi range: the latest value of whichever
// series a chart declares HEADLINE, and the time window the axis runs.
// `WagmiLineChart.tsx` had this inline; `WagmiSpikeChart.tsx` needed the
// exact same wording for the exact same reason (a single-series chart is
// still a chart with a headline, just one with only one candidate). Pulled
// out here so both call ONE function, never two copies of the same two `t()`
// calls that could drift apart.
//
// `t`/`dateFmt` are passed in rather than built here: this stays pure
// (provable without mounting anything), the caller still owns its own
// `useTranslation()` and `Intl.DateTimeFormat` instances.
export function buildChartA11ySummary(args: {
  t: TFn;
  dateFmt: Intl.DateTimeFormat;
  lo: number;
  hi: number;
  x0: number;
  xEnd: number;
  /** The headline series' latest value, or `undefined` when there is no
   *  headline (an empty chart), never a placeholder number. */
  last: number | undefined;
  yFormat: (y: number) => string;
}): string {
  const { t, dateFmt, lo, hi, x0, xEnd, last, yFormat } = args;
  const windowLabel = t("progress.chartWindowA11y", {
    defaultValue: "{{from}} to {{to}}",
    from: dateFmt.format(new Date(x0)),
    to: dateFmt.format(new Date(xEnd)),
  });
  return t("progress.chartSummaryA11y", {
    defaultValue: "{{range}}, latest {{last}}, {{window}}",
    range: `${yFormat(lo)} – ${yFormat(hi)}`,
    last: last === undefined ? "" : yFormat(last),
    window: windowLabel,
  });
}
